import { Input } from '@src/components/ui/input';

import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldPhoto,
} from '@shared/types/process';

import { ProcessFieldPhotoPreview } from './ProcessFieldPhotoPreview';
import { getPhotoAccept } from './processFieldPhotoUtils';
import { useProcessFieldPhotoFiles } from './useProcessFieldPhotoFiles';

import { ShadcnForm } from '@shadcn-features/core';
import { Paperclip } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type PhotoFieldProps = {
    field: ProcessFieldBase & ProcessFieldPhoto;
    initialValue?: string[];
    label?: React.ReactNode;
    name: string;
    readOnly?: boolean;
    required?: boolean;
};

type PhotoInputProps = Pick<PhotoFieldProps, 'field' | 'readOnly'> & {
    onChange: (value: string[]) => void;
    value: unknown;
};

function ProcessFieldPhotoInput({
    field,
    onChange,
    readOnly,
    value,
}: PhotoInputProps) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const photoIds = useMemo(() => parsePhotoIds(value), [value]);
    const max = field.options.max ?? 1;
    const maxSize = field.options.maxSize ?? 10;
    const acceptTypes = useMemo(
        () => field.options.acceptTypes ?? ['Jpeg', 'Jpg', 'Png'],
        [field.options.acceptTypes],
    );
    const { handleFiles, isUploading, removePhoto } = useProcessFieldPhotoFiles(
        {
            acceptTypes,
            max,
            maxSize,
            onChange,
            photoIds,
        },
    );

    if (readOnly && photoIds.length === 0) {
        return (
            <div className='flex min-h-[65px] items-center rounded-xl bg-(--color-body) p-[15px] text-sm text-(--color-card-subtitle)'>
                {t('translation:noFile')}
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-3'>
            {!readOnly && photoIds.length < max && (
                <div
                    className={`relative flex h-(--input-size) items-center rounded-lg border border-(--color-brand-green)! ${
                        isUploading
                            ? 'cursor-default! opacity-50'
                            : 'cursor-pointer hover:bg-(--color-bg-primary) hover:text-(--color-brand-green)'
                    }`}
                    onClick={() => {
                        if (!isUploading) inputRef.current?.click();
                    }}
                >
                    <div className='pointer-events-auto absolute inset-0 flex w-fit items-center gap-4 px-4'>
                        <Paperclip
                            aria-hidden
                            className='text-(--color-brand-green)'
                            size={20}
                        />
                        <span className='text-sm font-medium text-nowrap'>
                            {t('translation:selectFile')}
                        </span>
                    </div>
                    <div
                        className='h-full w-full'
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                            event.preventDefault();
                            if (isUploading) return;
                            void handleFiles(
                                Array.from(event.dataTransfer.files),
                            );
                        }}
                    >
                        <Input
                            ref={inputRef}
                            accept={getPhotoAccept(acceptTypes)}
                            className='pointer-events-none absolute h-0 w-0 opacity-0'
                            disabled={isUploading}
                            multiple
                            type='file'
                            onChange={(event) => {
                                const files = Array.from(
                                    event.target.files ?? [],
                                );
                                void handleFiles(files);
                                event.target.value = '';
                            }}
                        />
                    </div>
                </div>
            )}
            {photoIds.length > 0 && (
                <div className='flex w-full flex-col gap-2.5'>
                    {photoIds.map((id) => (
                        <ProcessFieldPhotoPreview
                            id={id}
                            key={id}
                            readOnly={readOnly}
                            onDelete={removePhoto}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export function ProcessFieldPhotoField({
    field,
    initialValue,
    label,
    name,
    readOnly,
    required,
}: PhotoFieldProps) {
    const getLangText = useLangText();

    return (
        <ShadcnForm.Item
            defaultValue={initialValue}
            label={label}
            name={name}
            readOnly={readOnly}
            required={required}
            tooltip={getLangText(field.tooltip ?? '')}
        >
            {({ value, onChange }) => (
                <ProcessFieldPhotoInput
                    field={field}
                    readOnly={readOnly}
                    value={value}
                    onChange={onChange}
                />
            )}
        </ShadcnForm.Item>
    );
}

function isString(value: unknown): value is string {
    return typeof value === 'string';
}

function parsePhotoIds(value: unknown): string[] {
    if (Array.isArray(value)) return value.filter(isString);
    if (typeof value !== 'string') return [];

    try {
        const parsed = JSON.parse(value);

        return Array.isArray(parsed) ? parsed.filter(isString) : [];
    } catch {
        return [];
    }
}
